<template>
  <div class="md:page">
    <SideBar>
      <div class="h-screen relative">
        <div class="text-left w-3/4 mx-auto absolute z-10 left-10 xl:left-20">
          <div class="text-grey-2 font-medium text-3xl">
            {{ $t('giftcards.organization.sidebar') }}
          </div>
          <div style="max-width: 350px" class="mx-auto mt-8">
            <Card :name="companyName" :amount="amount" template="default" />
          </div>
          <p v-if="cardCount > 1" class="mt-4 text-grey-3 text-sm text-center">
            {{ $t('giftcards.organization.preview_count', { count: cardCount }) }}
          </p>
        </div>
      </div>
    </SideBar>
    <div class="w-full lg:w-2/3 ml-auto min-h-screen relative text-left md:pt-24">
      <div class="steps w-4/5 mx-auto pt-4 md:pt-0 pb-32">
        <div class="flex md:hidden pb-4 mb-6 border-b border-grey-6">
          <img
            src="@/assets/svg/arrows/left_grey_arrow.svg"
            alt=""
            class="mr-5 mt-1 cursor-pointer"
            @click="back"
          />
          <p class="text-xl font-bold text-grey-2 ml-0">
            {{ step === 'order' ? $t('giftcards.organization.title') : $t('giftcards.organization.review.title') }}
          </p>
        </div>

        <template v-if="step === 'order'">
          <div class="steps__title hidden md:block">{{ $t('giftcards.organization.title') }}</div>
          <div class="steps__subtitle mt-5 mb-8">
            {{ $t('giftcards.organization.subtitle', { company: companyName }) }}
          </div>

          <Form class="form" v-slot="{ errors, meta }">
            <div class="text-grey-giftcard-title font-medium mb-2">
              {{ $t('giftcards.organization.fields.number.title') }}
            </div>
            <GeneralInput
              v-model:data="number"
              type="number"
              name="number"
              :label="$t('giftcards.organization.fields.number.placeholder')"
              rules="required|minValue:1,0"
              :error="errors.number || (tooMany ? $t('giftcards.organization.fields.number.max', { max: maxCards }) : null)"
              class="mb-6"
            />

            <div class="text-grey-giftcard-title font-medium mb-2">
              {{ $t('giftcards.organization.fields.amount.title') }}
            </div>
            <GeneralInput
              v-model:data="amount"
              type="dollar"
              name="amount"
              :label="$t('giftcards.organization.fields.amount.placeholder')"
              rules="required|minValue:2000|maxValue:100000"
              :error="errors.amount"
              class="mb-6"
            />

            <div v-if="orderReady && wallet" id="gp-giftcards-organization-summary">
              <Table :data="summaryTable" />

              <div
                v-if="missing > 0"
                class="rounded-2xl bg-orange-sun-6 border border-orange-3 p-5 mt-6"
              >
                <p class="font-semibold text-grey-2">
                  {{ $t('giftcards.organization.funding.missing.title', { amount: '$' + dollarFormatting(missing) }) }}
                </p>
                <p class="text-sm text-grey-3 mt-2">
                  {{ $t('giftcards.organization.funding.missing.description') }}
                </p>
                <gp-button
                  id="gp-button-app-giftcards-organization-topup"
                  class="mt-4"
                  @click="topup"
                >
                  {{ $t('giftcards.organization.funding.missing.button', { amount: '$' + dollarFormatting(topupAmount) }) }}
                </gp-button>
              </div>
              <div
                v-else-if="shortfall > 0"
                class="rounded-2xl bg-grey-6 p-5 mt-6"
              >
                <p class="font-semibold text-grey-2">{{ $t('giftcards.organization.funding.pending.title') }}</p>
                <p class="text-sm text-grey-3 mt-2">{{ $t('giftcards.organization.funding.pending.description') }}</p>
              </div>
              <div
                v-else
                class="rounded-2xl bg-green-5 p-5 mt-6"
              >
                <p class="font-semibold text-grey-2">{{ $t('giftcards.organization.funding.covered.title') }}</p>
              </div>
            </div>

            <p v-if="!isKycRegular" class="text-sm text-red-500 mt-6">
              {{ $t('giftcards.organization.not_verified') }}
            </p>
            <p class="text-sm text-grey-4 mt-6">
              {{ $t('giftcards.organization.limit', { limit: '$' + dollarFormatting(yearlyLimit, false) }) }}
            </p>

            <div class="mt-10 lg:flex">
              <gp-button
                id="gp-button-app-giftcards-organization-review"
                class="ml-auto"
                :disabled="!meta.valid || !orderReady || tooMany || shortfall > 0 || !isKycRegular"
                @click="step = 'review'"
              >
                {{ $t('giftcards.organization.review_button') }}
              </gp-button>
            </div>
          </Form>
        </template>

        <template v-else>
          <div class="steps__title hidden md:block">{{ $t('giftcards.organization.review.title') }}</div>
          <div class="steps__subtitle mt-5 mb-8">
            {{ $t('giftcards.organization.review.subtitle', { email: profile.email }) }}
          </div>
          <Table :data="reviewTable" />
          <div class="lg:flex mt-12 gap-4 w-full">
            <gp-button
              id="gp-button-app-giftcards-organization-back"
              color="white"
              class="mb-4 md:mb-0"
              @click="step = 'order'"
            >
              {{ $t('wording.back') }}
            </gp-button>
            <gp-button
              id="gp-button-app-giftcards-organization-confirm"
              class="ml-auto"
              @click="confirm"
            >
              {{ $t('giftcards.organization.review.button', { amount: '$' + dollarFormatting(total) }) }}
            </gp-button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { Form } from 'vee-validate'
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import SideBar from '@/components/general/SideBar.vue'
import GeneralInput from '@/components/general/Input.vue'
import GpButton from '@/components/general/GpButton'
import Table from '@/components/general/Table'
import Card from '@/components/giftcards/Card'
import useGlobalHelpers from '@/mixins/useGlobalHelpers'

// Order is kept in the browser so an organization can top up, wait for the money and come back to finish
const DRAFT_KEY = 'giftcard_organization_draft'
const MAX_CARDS = 250

export default {
  components: { Form, SideBar, GeneralInput, GpButton, Table, Card },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const i18n = useI18n()
    const { dollarFormatting } = useGlobalHelpers()

    const step = ref('order')
    const number = ref('')
    const amount = ref('')

    const profile = computed(() => store.getters.getProfile())
    const wallet = computed(() => store.state.wallets.wallet)
    const companyName = computed(() => (profile.value && profile.value.company_name) || '')
    const isKycRegular = computed(() => profile.value && profile.value.kyc_level === 'regular')

    // All money below is in cents, like the API
    const cardCount = computed(() => parseInt(number.value) || 0)
    const amountCents = computed(() =>
      amount.value ? Math.round(parseFloat(amount.value.toString().replace(/,/g, '')) * 100) || 0 : 0
    )
    const total = computed(() => cardCount.value * amountCents.value)
    const balance = computed(() => (wallet.value ? Math.round(wallet.value.balance) || 0 : 0))
    const pending = computed(() => (wallet.value ? Math.max(0, wallet.value.pending_balance || 0) : 0))
    // What the wallet can't pay right now
    const shortfall = computed(() => Math.max(0, total.value - balance.value))
    // What still has to be added once pending top-ups are counted
    const missing = computed(() => Math.max(0, shortfall.value - pending.value))
    const settings = computed(() => store.state.general.settings || {})
    const yearlyLimit = computed(() => parseInt(settings.value.giftcard_yearly_limit_company) || 500000)

    // The top-up form won't accept less than its minimum, so never preset below it
    const minTopup = computed(() => parseInt(settings.value.min_topup) || 1000)
    const topupAmount = computed(() => Math.max(missing.value, minTopup.value))
    const tooMany = computed(() => cardCount.value > MAX_CARDS)
    const orderReady = computed(() => cardCount.value > 0 && amountCents.value > 0)

    const summaryTable = computed(() => {
      const top = [
        {
          title: i18n.t('giftcards.organization.summary.total', { count: cardCount.value, amount: '$' + dollarFormatting(amountCents.value) }),
          value: '$' + dollarFormatting(total.value)
        },
        {
          title: i18n.t('giftcards.organization.summary.balance'),
          value: '$' + dollarFormatting(balance.value)
        }
      ]
      if (pending.value > 0) {
        top.push({
          title: i18n.t('giftcards.organization.summary.pending'),
          value: '$' + dollarFormatting(pending.value)
        })
      }
      return { top }
    })

    const reviewTable = computed(() => ({
      top: [
        { title: i18n.t('giftcards.organization.review.number'), value: cardCount.value },
        { title: i18n.t('giftcards.organization.review.amount'), value: '$' + dollarFormatting(amountCents.value) },
        { title: i18n.t('giftcards.organization.review.total'), value: '$' + dollarFormatting(total.value) },
        { title: i18n.t('giftcards.confirm.table.method'), value: i18n.t('giftcards.method.wallet.title') }
      ],
      bottom: [
        { title: i18n.t('giftcards.confirm.table.balance'), value: '$' + dollarFormatting(balance.value - total.value) }
      ]
    }))

    function saveDraft() {
      try {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ number: number.value, amount: amount.value }))
      } catch (e) {
        // Storage can be unavailable (private mode); the order just won't be remembered
      }
    }

    function clearDraft() {
      try {
        localStorage.removeItem(DRAFT_KEY)
      } catch (e) {
        // Nothing to clear
      }
    }

    function loadDraft() {
      try {
        const draft = JSON.parse(localStorage.getItem(DRAFT_KEY))
        if (draft) {
          number.value = draft.number || ''
          amount.value = draft.amount || ''
        }
      } catch (e) {
        // Ignore an unreadable draft
      }
    }

    watch([number, amount], saveDraft)

    function topup() {
      saveDraft()
      router.push({ name: 'topup', params: { propsAmount: String(topupAmount.value) } })
    }

    function back() {
      if (step.value === 'review') step.value = 'order'
      else router.push({ name: 'dashboard' })
    }

    function confirm() {
      const locale = i18n.locale.value
      const formData = new FormData()
      formData.append('personal', false)
      formData.append('number', cardCount.value)
      formData.append('amount', amountCents.value)
      formData.append('email', profile.value.email)
      formData.append('campaign_id', 2)
      formData.append('language', locale)
      formData.append('extra_info', encodeURIComponent(JSON.stringify({ message: '', template: 'default', language: locale })))
      formData.append('redirect_url', process.env.VUE_APP_PUBLIC_PAGES_URL + 'giftcards?success=true')

      store.dispatch('setLoading', true)
      axios
        .post(process.env.VUE_APP_SERVER_BASE_URL + 'api/v1/giftcards', formData)
        .then(() => {
          clearDraft()
          store.dispatch('setLoading', false)
          store.dispatch('fetchWallets')
          router.push({ name: 'dashboard', query: { boughtgift: 1 } })
        })
        .catch((e) => {
          store.dispatch('setLoading', false)
          store.dispatch('setError', e)
        })
    }

    onMounted(async () => {
      // A link from the public organizations page carries the order (amount in cents); otherwise resume a saved one
      if (route.query.number || route.query.amount) {
        number.value = route.query.number ? String(parseInt(route.query.number) || '') : ''
        amount.value = route.query.amount ? dollarFormatting(parseInt(route.query.amount)) || '' : ''
      } else {
        loadDraft()
      }
      // The wallets response also carries these settings back (see the AddSettings middleware)
      await store.dispatch('setRequestSettings', 'min_topup,giftcard_yearly_limit_company')
      await store.dispatch('fetchWallets')
    })

    return {
      step,
      number,
      amount,
      profile,
      wallet,
      companyName,
      isKycRegular,
      cardCount,
      total,
      shortfall,
      missing,
      topupAmount,
      tooMany,
      orderReady,
      maxCards: MAX_CARDS,
      yearlyLimit,
      summaryTable,
      reviewTable,
      dollarFormatting,
      topup,
      back,
      confirm
    }
  }
}
</script>
